import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import '../styles/services.css';
import '../styles/responsive-utils.css';
import '../styles/customer-responsive.css';
import '../styles/services-responsive.css';

// Deduplicate services by name — one card per unique service name, with global price range
const deduplicateServicesByName = (servicesList) => {
  const groups = {};
  servicesList.forEach((svc) => {
    const key = svc.name;
    if (!groups[key]) groups[key] = [];
    groups[key].push(svc);
  });

  return Object.values(groups).map((group) => {
    const base = group[0];
    const imageUrl = group.find((s) => s.imageUrl)?.imageUrl || null;

    const prices = group.map((s) => {
      const lo = s.priceMin ?? s.price ?? null;
      const hi = s.priceMax ?? s.price ?? null;
      return { lo, hi };
    }).filter((p) => p.lo != null);

    const globalMin = prices.length ? Math.min(...prices.map((p) => Number(p.lo))) : null;
    const globalMax = prices.length ? Math.max(...prices.map((p) => Number(p.hi ?? p.lo))) : null;

    return {
      ...base,
      imageUrl,
      priceMin: globalMin,
      priceMax: globalMax,
      price: undefined,
    };
  });
};

function ServicesBrowsing() {
  const { salonId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [salonInfo, setSalonInfo] = useState(null);
  const [error, setError] = useState(null);
  // #Revised UI_v2 — CE4: staff count per service name
  const [staffCountMap, setStaffCountMap] = useState({});

  useEffect(() => {
    const fetchServicesAndCategories = async () => {
      try {
        setLoading(true);
        
        // Get salon information
        const salonDocRef = doc(db, 'salons', salonId);
        const salonDoc = await getDoc(salonDocRef);
        
        if (!salonDoc.exists()) {
          throw new Error('Salon not found');
        }
        
        setSalonInfo(salonDoc.data());
        
        // Get services filtered by salonId
        const servicesQuery = query(collection(db, 'services'), where('salonId', '==', salonId));
        const servicesSnapshot = await getDocs(servicesQuery);

        const servicesList = [];
        const categoriesSet = new Set();

        servicesSnapshot.forEach((doc) => {
          const serviceData = doc.data();
          if (serviceData.isActive !== false) {
            servicesList.push({ id: doc.id, ...serviceData });
            if (serviceData.category) categoriesSet.add(serviceData.category);
          }
        });

        setServices(servicesList);
        setCategories(Array.from(categoriesSet).sort());

        // #Revised UI_v2 — CE4: load staff and build service → staff count map
        const staffQuery = query(collection(db, 'staff'), where('salonId', '==', salonId));
        const staffSnapshot = await getDocs(staffQuery);
        const activeStaff = staffSnapshot.docs
          .map(d => d.data())
          .filter(s => s.isActive !== false);

        const countMap = {};
        servicesList.forEach(svc => {
          countMap[svc.name] = activeStaff.filter(
            s => Array.isArray(s.services) && s.services.includes(svc.name)
          ).length;
        });
        setStaffCountMap(countMap);
        
      } catch (error) {
        console.error('Error fetching services:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchServicesAndCategories();
  }, [salonId]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleBookService = (service) => {
    // Navigate to booking page with selected service
    navigate(`/salon/${salonId}/book`, { 
      state: { selectedService: service } 
    });
  };

  // Filter by category then deduplicate by name
  const filteredServices = deduplicateServicesByName(
    selectedCategory === 'all'
      ? services
      : services.filter(service => service.category === selectedCategory)
  );

  if (loading) {
    return <div className="services-loading">Loading services...</div>;
  }
  
  if (error) {
    return <div className="services-error">Error: {error}</div>;
  }

  return (
    <div className="services-browsing">
      <header className="services-header">
        <h1>Our Services</h1>
        {salonInfo && <p className="salon-name">{salonInfo.branding.name}</p>}
      </header>
      
      <div className="categories-filter">
        <button 
          className={selectedCategory === 'all' ? 'category-button active' : 'category-button'}
          onClick={() => handleCategoryChange('all')}
        >
          All Services
        </button>
        
        {categories.map(category => (
          <button 
            key={category}
            className={selectedCategory === category ? 'category-button active' : 'category-button'}
            onClick={() => handleCategoryChange(category)}
          >
            {category}
          </button>
        ))}
      </div>
      
      <div className="services-grid">
        {filteredServices.length === 0 ? (
          <div className="no-services">
            <p>No services found in this category.</p>
          </div>
        ) : (
          // #Revised UI_v2 — CE4: redesigned service card
          filteredServices.map(service => (
            <div key={service.id} className="service-card">
              {/* Larger image area with category overlay */}
              <div className="service-image">
                {service.imageUrl
                  ? <img src={service.imageUrl} alt={service.name} />
                  : <div className="service-image-placeholder" />
                }
                {service.category && (
                  <span className="service-category-overlay">{service.category}</span>
                )}
              </div>

              <div className="service-content">
                <h2 className="service-name">{service.name}</h2>

                {/* Price + duration + staff count as paired badge row */}
                <div className="service-badge-row">
                  <span className="service-badge service-badge--price">
                    {service.priceMin != null
                      ? (service.priceMin === service.priceMax
                          ? `$${Number(service.priceMin).toFixed(2)}`
                          : `$${Number(service.priceMin).toFixed(2)} – $${Number(service.priceMax).toFixed(2)}`)
                      : 'N/A'}
                  </span>
                  <span className="service-badge service-badge--duration">
                    ⏱ {service.duration || 'N/A'} min
                  </span>
                  {staffCountMap[service.name] !== undefined && (
                    <span className="service-badge service-badge--staff">
                      👤 {staffCountMap[service.name]} staff
                    </span>
                  )}
                </div>

                <p className="service-description">{service.description}</p>

                <button
                  className="book-service-button"
                  onClick={() => handleBookService(service)}
                >
                  Book Now
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ServicesBrowsing;
